import type { LawSearchResponse, LawDetail, LawType, SearchResult } from '@/types/law'

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api'

async function apiFetch<T>(path: string, params?: Record<string, string>): Promise<T> {
  const url = new URL(`${API_BASE}${path}`, window.location.origin)
  if (params) {
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v))
  }
  const res = await fetch(url.toString())
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText })) as { error?: string }
    throw new Error(err.error ?? `API 오류: ${res.status}`)
  }
  return res.json() as Promise<T>
}

export async function searchLaw(
  query: string,
  type: LawType | 'all' = 'law',
  page = 1,
  size = 20
): Promise<{ results: SearchResult[]; total: number }> {
  if (type === 'all') {
    const data = await apiFetch<{ results: Record<string, LawSearchResponse> }>('/search/all', { q: query })
    const results: SearchResult[] = []
    Object.entries(data.results).forEach(([lawType, resp]) => {
      results.push(...normalizeSearchResponse(resp, lawType as LawType))
    })
    return { results, total: results.length }
  }
  const data = await apiFetch<LawSearchResponse>('/search', {
    q: query, type, page: String(page), size: String(size),
  })
  return {
    results: normalizeSearchResponse(data, type),
    total: Number(data.LawSearch?.totalCnt ?? 0),
  }
}

export async function suggestLawNames(query: string): Promise<string[]> {
  if (!query) return []
  const data = await apiFetch<LawSearchResponse>('/suggest', { q: query })
  return (data.LawSearch?.law ?? []).map((i) => i.법령명한글 ?? '').filter(Boolean)
}

export async function getLawDetail(mst: string): Promise<LawDetail> {
  return apiFetch<LawDetail>(`/law/${mst}`)
}

export async function getLawCompare(mst: string): Promise<unknown> {
  return apiFetch(`/law/${mst}/compare`)
}

export async function getLawTree(mst: string): Promise<unknown> {
  return apiFetch(`/law/${mst}/tree`)
}

export async function healthCheck(): Promise<{ status: string; hasApiKey: boolean }> {
  return apiFetch('/health')
}

function normalizeSearchResponse(data: LawSearchResponse, type: LawType): SearchResult[] {
  const items = data.LawSearch?.law ?? []
  return items.map((item): SearchResult => {
    if (item.법령일련번호) {
      return {
        id: item.법령일련번호,
        title: item.법령명한글 ?? '(제목 없음)',
        subtitle: item.소관부처명,
        type,
        date: item.시행일자,
        ministry: item.소관부처명,
        raw: item,
      }
    }
    if (item.판례정보일련번호) {
      return {
        id: item.판례정보일련번호,
        title: item.사건명 ?? item.사건번호 ?? '(사건명 없음)',
        subtitle: item.법원명,
        type: 'prec',
        date: item.선고일자,
        raw: item,
      }
    }
    if (item.행정규칙일련번호) {
      return {
        id: item.행정규칙일련번호,
        title: item.행정규칙명 ?? '(제목 없음)',
        subtitle: item.발령기관명,
        type: 'admrul',
        raw: item,
      }
    }
    return { id: String(Math.random()), title: '(제목 없음)', type, raw: item }
  })
}
